import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { AudioButton } from "@/components/AudioButton";
import { PageTip } from "@/components/PageTip";
import {
  Play,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Flame,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  getReviewBatch,
  updateReviewResult,
  getReviewStats,
  getCategories,
  getStudySets,
  getStudySetReviewBatch,
  updateStreak,
  getFsrsSchedulingOptions,
} from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { Word, ReviewRating, StreakData } from "@/types";
import { cn } from "@/lib/utils";

type SessionState = "idle" | "active" | "complete";
type ReviewMode = "all" | "category" | "studySet";

function formatTimeUntil(date: Date): string {
  const now = Date.now();
  const target = date instanceof Date ? date.getTime() : new Date(date).getTime();
  const diffMs = target - now;
  if (diffMs <= 0) return "now";
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return "in less than an hour";
  if (hours < 24) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

export function ReviewPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const [batchSize, setBatchSize] = useState(10);
  const [reviewMode, setReviewMode] = useState<ReviewMode>("all");
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [selectedStudySetId, setSelectedStudySetId] = useState<string | undefined>();
  const [words, setWords] = useState<Word[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [results, setResults] = useState<Array<{ wordId: string; rating: ReviewRating }>>([]);
  const [direction, setDirection] = useState<"left" | "right" | null>(null);
  const [emptyBatchError, setEmptyBatchError] = useState<string | null>(null);
  const [sessionStreak, setSessionStreak] = useState<StreakData | null>(null);

  const queryClient = useQueryClient();

  // Handle URL parameters for category or study set
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const studySetParam = searchParams.get("studySet");

    if (studySetParam) {
      setReviewMode("studySet");
      setSelectedStudySetId(studySetParam);
      setSelectedCategory(undefined);
    } else if (categoryParam) {
      setReviewMode("category");
      setSelectedCategory(categoryParam);
      setSelectedStudySetId(undefined);
    }
  }, [searchParams]);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["reviewStats", user?.uid],
    queryFn: () => getReviewStats(user!.uid),
    enabled: !!user,
  });

  // Fetch categories for filtering
  const { data: categories } = useQuery({
    queryKey: ["categories", user?.uid],
    queryFn: () => getCategories(user!.uid),
    enabled: !!user,
  });

  // Fetch study sets for filtering
  const { data: studySets } = useQuery({
    queryKey: ["studySets", user?.uid],
    queryFn: () => getStudySets(user!.uid),
    enabled: !!user,
  });

  const selectedStudySet = studySets?.find((s) => s.id === selectedStudySetId);

  // Fisher-Yates shuffle
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Start session mutation
  const startMutation = useMutation({
    mutationFn: async () => {
      setEmptyBatchError(null);
      if (reviewMode === "studySet" && selectedStudySetId) {
        return getStudySetReviewBatch(user!.uid, selectedStudySetId, batchSize);
      }
      return getReviewBatch(user!.uid, batchSize, selectedCategory);
    },
    onSuccess: (data) => {
      if (data.length > 0) {
        // Shuffle words before starting the session
        setWords(shuffleArray(data));
        setCurrentIndex(0);
        setShowAnswer(false);
        setResults([]);
        setSessionState("active");
      } else {
        // Handle empty batch
        if (reviewMode === "studySet" && selectedStudySet) {
          setEmptyBatchError(`No words found in "${selectedStudySet.name}". Add some words to this study set first.`);
        } else if (reviewMode === "category" && selectedCategory) {
          setEmptyBatchError(`No words found in category "${selectedCategory}".`);
        } else {
          setEmptyBatchError("No words available to review. Add some words first.");
        }
      }
    },
  });

  // Update result mutation
  const resultMutation = useMutation({
    mutationFn: ({ wordId, rating, word }: { wordId: string; rating: ReviewRating; word: Word }) =>
      updateReviewResult(user!.uid, wordId, rating, word.reviewStats),
  });

  const currentWord = words[currentIndex];

  // Get FSRS scheduling preview for interval display on buttons
  const schedulingOptions = currentWord ? getFsrsSchedulingOptions(currentWord.reviewStats) : null;

  const handleAnswer = async (rating: ReviewRating) => {
    if (!currentWord) return;

    setDirection(rating === 1 ? "left" : "right");

    // Record result
    setResults([...results, { wordId: currentWord.id, rating }]);

    // Update backend
    resultMutation.mutate({ wordId: currentWord.id, rating, word: currentWord });

    // Wait for animation
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Move to next card or complete
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
      setDirection(null);
    } else {
      setDirection(null);
      setSessionState("complete");
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["reviewStats"] });
      // Update streak
      updateStreak(user!.uid).then(setSessionStreak).catch(console.error);
    }
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      handleAnswer(3); // Good
    } else if (info.offset.x < -threshold) {
      handleAnswer(1); // Again
    }
  };

  const restartSession = () => {
    setSessionState("idle");
    setWords([]);
    setResults([]);
    setCurrentIndex(0);
    setShowAnswer(false);
    setDirection(null);
  };

  // Review the same words again (shuffle and restart)
  const reviewSameWords = () => {
    setWords(shuffleArray(words));
    setCurrentIndex(0);
    setShowAnswer(false);
    setResults([]);
    setDirection(null);
    setSessionState("active");
  };

  // Keyboard controls
  useEffect(() => {
    if (sessionState !== "active") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for arrow keys and space to avoid page scrolling
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", " ", "1", "2", "3", "4"].includes(e.key)) {
        e.preventDefault();
      }

      if (e.key === " ") {
        // Toggle answer visibility
        setShowAnswer((prev) => !prev);
      } else if (showAnswer && currentWord) {
        // FSRS ratings via number keys
        if (e.key === "1") handleAnswer(1); // Again
        else if (e.key === "2") handleAnswer(2); // Hard
        else if (e.key === "3") handleAnswer(3); // Good
        else if (e.key === "4") handleAnswer(4); // Easy
        // Arrow keys: left=Again, right=Good
        else if (e.key === "ArrowLeft") handleAnswer(1);
        else if (e.key === "ArrowRight") handleAnswer(3);
      } else if (!showAnswer) {
        // Any arrow key or space reveals
        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          setShowAnswer(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sessionState, showAnswer, currentWord]);

  // Idle State - Session Setup
  if (sessionState === "idle") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Practice</h1>
          <p className="text-muted-foreground">
            Practice your vocabulary with flashcards
          </p>
        </div>
        <PageTip pageKey="practice" message="Flashcards show words that are due for review. Swipe right or tap 'Good' if you know it, left or 'Again' if you don't. The app schedules each word at the perfect time so you never forget." />

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <Card className={stats.dueCount > 0 ? "border-primary" : ""}>
              <CardContent className="pt-4 text-center">
                <div className={cn("text-3xl font-bold", stats.dueCount > 0 && "text-primary")}>
                  {stats.dueCount}
                </div>
                <div className="text-sm text-muted-foreground">Due Now</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-3xl font-bold">{stats.totalWords}</div>
                <div className="text-sm text-muted-foreground">Total Words</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-3xl font-bold">
                  {Math.round(stats.averageSuccessRate * 100)}%
                </div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Session Setup */}
        <Card data-walkthrough="practice-start">
          <CardHeader>
            <CardTitle>Start Review Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Review Mode Tabs */}
            <div>
              <label className="text-sm font-medium">Review Mode</label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant={reviewMode === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setReviewMode("all");
                    setSelectedCategory(undefined);
                    setSelectedStudySetId(undefined);
                    setEmptyBatchError(null);
                  }}
                >
                  All Words
                </Button>
                <Button
                  variant={reviewMode === "category" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setReviewMode("category");
                    setSelectedStudySetId(undefined);
                    setEmptyBatchError(null);
                  }}
                >
                  By Category
                </Button>
                <Button
                  variant={reviewMode === "studySet" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setReviewMode("studySet");
                    setSelectedCategory(undefined);
                    setEmptyBatchError(null);
                  }}
                >
                  By Study Set
                </Button>
              </div>
            </div>

            {/* Category Selection */}
            {reviewMode === "category" && (
              <div>
                <label className="text-sm font-medium">Select Category</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {categories && categories.length > 0 ? (
                    categories.map((cat) => (
                      <Button
                        key={cat.id}
                        variant={selectedCategory === cat.name ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(cat.name)}
                      >
                        {cat.name} ({cat.wordCount})
                      </Button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No categories yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Study Set Selection */}
            {reviewMode === "studySet" && (
              <div>
                <label className="text-sm font-medium">Select Study Set</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {studySets && studySets.length > 0 ? (
                    studySets.map((set) => (
                      <Button
                        key={set.id}
                        variant={selectedStudySetId === set.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedStudySetId(set.id)}
                      >
                        {set.name} ({set.wordIds.length})
                      </Button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No study sets yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Batch Size */}
            <div>
              <label className="text-sm font-medium">Cards per session</label>
              <div className="flex gap-2 mt-2">
                {[10, 15, 20].map((size) => (
                  <Button
                    key={size}
                    variant={batchSize === size ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBatchSize(size)}
                  >
                    {size}
                  </Button>
                ))}
              </div>
            </div>

            {/* Start Button */}
            <Button
              className="w-full"
              size="lg"
              onClick={() => startMutation.mutate()}
              disabled={
                startMutation.isPending ||
                (stats?.totalWords || 0) === 0 ||
                (stats?.dueCount || 0) === 0 ||
                (reviewMode === "category" && !selectedCategory) ||
                (reviewMode === "studySet" && !selectedStudySetId)
              }
            >
              {startMutation.isPending ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <Play className="mr-2 h-5 w-5" />
              )}
              {stats && stats.dueCount > 0
                ? `Review ${Math.min(batchSize, stats.dueCount)} Words`
                : "No Words Due"}
              {reviewMode === "category" && selectedCategory && ` - ${selectedCategory}`}
              {reviewMode === "studySet" && selectedStudySet && ` - ${selectedStudySet.name}`}
            </Button>

            {(stats?.totalWords || 0) === 0 && (
              <p className="text-sm text-muted-foreground text-center">
                Add some words first to start reviewing
              </p>
            )}

            {stats && stats.totalWords > 0 && stats.dueCount === 0 && stats.nextDueAt && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  Next word due {formatTimeUntil(stats.nextDueAt)}
                </span>
              </div>
            )}

            {reviewMode === "category" && !selectedCategory && (
              <p className="text-sm text-muted-foreground text-center">
                Select a category to start reviewing
              </p>
            )}

            {reviewMode === "studySet" && !selectedStudySetId && (
              <p className="text-sm text-muted-foreground text-center">
                Select a study set to start reviewing
              </p>
            )}

            {emptyBatchError && (
              <p className="text-sm text-destructive text-center">
                {emptyBatchError}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Complete State - Results
  if (sessionState === "complete") {
    const againCount = results.filter((r) => r.rating === 1).length;
    const hardCount = results.filter((r) => r.rating === 2).length;
    const goodCount = results.filter((r) => r.rating === 3).length;
    const easyCount = results.filter((r) => r.rating === 4).length;
    const successCount = hardCount + goodCount + easyCount;
    const percentage = results.length > 0 ? Math.round((successCount / results.length) * 100) : 0;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <Trophy className="h-16 w-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-semibold">Session Complete!</h1>
          <p className="text-muted-foreground">
            You reviewed {results.length} words
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-5 gap-2 text-center">
              <div>
                <div className="text-2xl font-bold text-red-600">{againCount}</div>
                <div className="text-xs text-muted-foreground">Again</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-500">{hardCount}</div>
                <div className="text-xs text-muted-foreground">Hard</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{goodCount}</div>
                <div className="text-xs text-muted-foreground">Good</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{easyCount}</div>
                <div className="text-xs text-muted-foreground">Easy</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{percentage}%</div>
                <div className="text-xs text-muted-foreground">Success</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {sessionStreak && sessionStreak.currentStreak > 0 && (
          <Card>
            <CardContent className="pt-6 flex items-center justify-center gap-3">
              <Flame className="h-6 w-6 text-orange-500" />
              <div className="text-center">
                <div className="text-2xl font-bold">{sessionStreak.currentStreak} day streak!</div>
                {sessionStreak.currentStreak === sessionStreak.longestStreak && sessionStreak.currentStreak > 1 && (
                  <div className="text-sm text-muted-foreground">New personal best!</div>
                )}
              </div>
              <Flame className="h-6 w-6 text-orange-500" />
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <Button className="flex-1" onClick={reviewSameWords}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Review Again
            </Button>
            <Button className="flex-1" onClick={() => startMutation.mutate()}>
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={restartSession}>
            Back to Setup
          </Button>
        </div>
      </div>
    );
  }

  // Active State - Flashcards
  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={restartSession}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Exit
        </Button>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">
            {currentIndex + 1} / {words.length}
          </div>
          {(selectedCategory || selectedStudySet) && (
            <Badge variant="outline" className="text-xs">
              {selectedCategory || selectedStudySet?.name}
            </Badge>
          )}
        </div>
        <div className="w-20" /> {/* Spacer for centering */}
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / words.length) * 100}%` }}
        />
      </div>

      {/* Flashcard */}
      <div className="relative h-[400px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          {currentWord && (
            <motion.div
              key={currentWord.id}
              initial={{ opacity: 0, scale: 0.9, x: 0 }}
              animate={{
                opacity: 1,
                scale: 1,
                x: direction === "right" ? 200 : direction === "left" ? -200 : 0,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={handleDragEnd}
              className="absolute w-full max-w-md cursor-grab active:cursor-grabbing"
            >
              <Card
                className={cn(
                  "h-[350px] flex flex-col items-center justify-center p-8 cursor-pointer select-none",
                  "transition-shadow hover:shadow-lg"
                )}
                onClick={() => setShowAnswer(!showAnswer)}
              >
                <CardContent className="text-center space-y-6 p-0">
                  {/* Hebrew (always shown) */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="text-4xl font-semibold hebrew-text" dir="rtl">
                      {currentWord.hebrew.text}
                    </div>
                    <AudioButton text={currentWord.hebrew.text} />
                  </div>

                  {/* Answer */}
                  <AnimatePresence>
                    {showAnswer && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-2"
                      >
                        <div className="text-2xl">
                          {currentWord.translations.en?.text ||
                            currentWord.translations.ru?.text}
                        </div>
                        <div className="flex gap-2 justify-center">
                          {currentWord.categories.slice(0, 2).map((cat) => (
                            <Badge key={cat} variant="secondary">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {!showAnswer && (
                    <p className="text-sm text-muted-foreground">
                      Tap to reveal answer
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Keyboard Hints */}
      <div className="text-center space-y-2 text-sm text-muted-foreground">
        {!showAnswer ? (
          <div>
            Tap card or press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Space</kbd> to reveal
          </div>
        ) : (
          <div className="text-xs opacity-75">
            Swipe left = don't know, right = know
          </div>
        )}
      </div>

      {/* FSRS Rating Buttons */}
      {showAnswer && schedulingOptions && (
        <div className="flex gap-2 justify-center">
          <Button
            variant="outline"
            className="flex-1 max-w-[100px] border-red-200 hover:bg-red-50 hover:text-red-700 flex-col h-auto py-2"
            onClick={() => handleAnswer(1)}
          >
            <span className="text-sm font-medium">Again</span>
            <span className="text-xs opacity-60">{schedulingOptions[1].interval}</span>
          </Button>
          <Button
            variant="outline"
            className="flex-1 max-w-[100px] border-orange-200 hover:bg-orange-50 hover:text-orange-700 flex-col h-auto py-2"
            onClick={() => handleAnswer(2)}
          >
            <span className="text-sm font-medium">Hard</span>
            <span className="text-xs opacity-60">{schedulingOptions[2].interval}</span>
          </Button>
          <Button
            variant="outline"
            className="flex-1 max-w-[100px] border-blue-200 hover:bg-blue-50 hover:text-blue-700 flex-col h-auto py-2"
            onClick={() => handleAnswer(3)}
          >
            <span className="text-sm font-medium">Good</span>
            <span className="text-xs opacity-60">{schedulingOptions[3].interval}</span>
          </Button>
          <Button
            variant="outline"
            className="flex-1 max-w-[100px] border-green-200 hover:bg-green-50 hover:text-green-700 flex-col h-auto py-2"
            onClick={() => handleAnswer(4)}
          >
            <span className="text-sm font-medium">Easy</span>
            <span className="text-xs opacity-60">{schedulingOptions[4].interval}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
